<template>
    <!-- Module Switcher Dialog -->
    <Dialog v-model:visible="isVisible" modal header="Switch Module" :style="{ width: '70vw' }" :breakpoints="{
        '1980px': '70vw',
        '1440px': '70vw',
        '960px': '80vw',
        '640px': '95vw',
    }" @after-hide="handleDialogClose">
        <b-container fluid>
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
                <div class="loading-spinner mb-3"></div>
                <p>Loading modules...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="text-center py-4">
                <i class="pi pi-exclamation-triangle text-warning mb-3" style="font-size: 2rem;"></i>
                <p class="text-danger">{{ error }}</p>
            </div>

            <!-- Modules List -->
            <div v-else>
                <!-- Accessible Modules -->
                <div v-if="processedModules.length > 0" class="mb-4">
                    <!-- <h5 class="mb-3">Available Modules</h5> -->
                    <b-row>
                        <b-col v-for="module in processedModules" :key="module.ID" lg="4" md="6" sm="12" class="mb-3">
                            <div class="module-card active" @click="handleModuleNavigation(module)">
                                <div class="module-card-content">
                                    <div class="module-icon-wrapper"
                                        :style="{ background: `linear-gradient(135deg, ${module.color}15, ${module.color}25)` }">
                                        <i :class="[module.icon]" :style="{ color: module.color }"></i>
                                    </div>
                                    <div class="module-info">
                                        <h6 class="module-name">{{ module.Name }}</h6>
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <!-- Empty State -->
                <div v-if="!processedModules.length" class="text-center py-5">
                    <i class="pi pi-box text-muted mb-3" style="font-size: 3rem;"></i>
                    <h5>No modules available</h5>
                    <p class="text-muted">No modules are currently available for your organization.</p>
                </div>
            </div>
        </b-container>
    </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import CommonService from '../../modules/crm/service/commonService';
import { useAuthStore } from '../../stores/auth.ts'
export default defineComponent({
    name: 'ModuleSwitcher',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        modules: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        }
    },
    emits: ['update:visible', 'close'],
    setup(props, { emit }) {
        const router = useRouter();

        // Computed property for v-model binding
        const isVisible = computed({
            get: () => props.visible,
            set: (value: boolean) => {
                emit('update:visible', value);
            }
        });
        // const authStore = useAuthStore()

        // Processed modules with icons, colors, and types
        const processedModules = ref<any[]>([]);

        // Watch for changes in modules prop and process them
        watch(() => props.modules, (newModules: any[]) => {
            // Process API modules
            const apiModules = newModules.map((module: any) => ({
                ...module,
                icon: getModuleIcon(module.Name || module.Name),
                color: getModuleColor(module.Name || module.Name),
                type: getModuleType(module.Name || module.Name)
            }));

            // Add MOPUI Dashboard module
            const dashboardModule = {
                ID: 999,
                Name: 'MOPUI Dashboard',
                isAccessible: true,
                icon: getModuleIcon('MOPUI Dashboard'),
                color: getModuleColor('MOPUI Dashboard'),
                type: getModuleType('MOPUI Dashboard')
            };

            // Combine dashboard with API modules
            processedModules.value = [dashboardModule, ...apiModules];
        }, { immediate: true });

        // Get user data from localStorage
        const getUserData = () => {
            const sessionUserData = localStorage.getItem("UsersData");
            return sessionUserData ? JSON.parse(sessionUserData) : null;
        };

        // Helper function to get module icon based on module name
        function getModuleIcon(moduleName: string): string {
            const name = moduleName?.toLowerCase() || '';
            if (name.includes('inventory')) return 'pi pi-box';
            if (name.includes('billing')) return 'pi pi-money-bill';
            if (name.includes('crm')) return 'pi pi-users';
            if (name.includes('communication')) return 'pi pi-comments';
            if (name.includes('user') || name.includes('management')) return 'pi pi-user-edit';
            if (name.includes('alert')) return 'pi pi-bell';
            if (name.includes('mopui') || name.includes('dashboard')) return 'pi pi-home';
            return 'pi pi-th-large'; // Default icon
        }

        // Helper function to get module color based on module name
        function getModuleColor(moduleName: string): string {
            const name = moduleName?.toLowerCase() || '';
            if (name.includes('inventory')) return '#10B981';
            if (name.includes('billing')) return '#F59E0B';
            if (name.includes('crm')) return '#8B5CF6';
            if (name.includes('communication')) return '#06B6D4';
            if (name.includes('user') || name.includes('management')) return '#EF4444';
            if (name.includes('alert')) return '#F97316';
            if (name.includes('mopui') || name.includes('dashboard')) return '#3B82F6';
            return '#6B7280'; // Default color
        }

        // Helper function to determine module type
        function getModuleType(moduleName: string): string {
            const name = moduleName?.toLowerCase() || '';

            // MoceroUI modules (only these should navigate within MoceroUI)
            const moceroUIModules = [
                'crm',
                'inventory',
                'billing',
                'communication',
                'alert engine',
                'queue management',
                'user management',
                'authorization'
            ];

            // Check if it's a MoceroUI module
            if (moceroUIModules.some(module => name.includes(module))) {
                return 'moceroui';
            }

            // Default to MOPUI for any other modules (including Accounts, Asset Management, Task Management, Dashboard, etc.)
            return 'mopui';
        }

        // Simplified module navigation based on MOPUI patterns
        async function handleModuleNavigation(module: any) {
            try {
                debugger
                // if (!module.isAccessible) return;

                console.log('Selected module:', module);

                // Close the dialog first
                isVisible.value = false;
                const moduleData = {
                    id: module.id,
                    name: module.name,
                    rights: [],
                    menu: { menus: [], subMenus: [] } // Initialize with empty menu structure
                }

                // authStore.setActiveModule(moduleData)
                // Handle different module types similar to MOPUI's handleModuleSelect
                if (module.type === 'mopui') {
                    // Navigate to MOPUI modules (cross-app navigation)
                    await handleMOPUIModule(module);
                } else {
                    // Navigate to MoceroUI modules (router-based navigation)
                    await handleMoceroUIModule(module);
                }

            } catch (error) {
                console.error('Error switching module:', error);
                // Error handling is now managed by parent component
            }
        }

        // Handle MOPUI module navigation (similar to MOPUI's handleMOPUIModule)
        async function handleMOPUIModule(module: any) {
            try {
                console.log('Handling MOPUI module:', module.Name);

                // Special handling for MOPUI Dashboard - navigate to MOPUI dashboard
                if (module.ID === 999) {
                    // Navigate to MOPUI dashboard (similar to how MOPUI navigates back to dashboard)
                    navigateToMOPUIDashboard();
                    return;
                }

                const moduleData = {
                    id: module.ID,
                    name: module.Name,
                    rights: [],
                    menu: { menus: [], subMenus: [] } // Initialize with empty menu structure
                }

                // authStore.setActiveModule(moduleData)

                // For other MOPUI modules, prepare session data and navigate
                prepareSessionForCrossApp(module, 'MOPUI');
                navigateToCrossApp(module, 'MOPUI');

            } catch (error) {
                console.error('Error handling MOPUI module:', error);
                throw new Error(`Failed to navigate to ${module.Name} module`);
            }
        }

        // Navigate to MOPUI Dashboard (similar to MOPUI's dashboard navigation)
        function navigateToMOPUIDashboard() {
            try {
                console.log('Navigating to MOPUI Dashboard');

                // Check if we're in development (different ports) or production (unified server)
                const isDevelopment = window.location.port === '5174';

                let mopuiUrl: string;

                if (isDevelopment) {
                    // Development: Use different port
                    mopuiUrl = 'http://localhost:5173';
                } else {
                    // Production: Use same server with /mopui/ path
                    mopuiUrl = `${window.location.origin}`;
                }

                // Get session data
                const usersDataStr = localStorage.getItem('UsersData');
                const rolesDataStr = localStorage.getItem('RolesData');

                // Navigate to MOPUI dashboard with session data
                const urlParams = new URLSearchParams({
                    crossApp: 'true',
                    sourceApp: 'MoceroUI',
                    returnToDashboard: 'true'
                });

                // Add session data as URL parameters (base64 encoded)
                if (usersDataStr) {
                    try {
                        const encodedUsersData = btoa(usersDataStr);
                        urlParams.set('usersData', encodedUsersData);
                    } catch (error) {
                        console.error('Error encoding usersData:', error);
                    }
                }

                if (rolesDataStr) {
                    try {
                        const encodedRolesData = btoa(rolesDataStr);
                        urlParams.set('rolesData', encodedRolesData);
                    } catch (error) {
                        console.error('Error encoding rolesData:', error);
                    }
                }

                // Navigate to MOPUI dashboard route
                const targetUrl = `${mopuiUrl}/#/dashboard?${urlParams.toString()}`;

                console.log('Navigating to MOPUI Dashboard:', targetUrl);

                // Navigate using window.location.href
                window.location.href = targetUrl;

            } catch (error) {
                console.error('Error navigating to MOPUI Dashboard:', error);
                throw new Error('Failed to navigate to MOPUI Dashboard');
            }
        }

        // Handle MoceroUI module navigation (simplified router-based approach)
        async function handleMoceroUIModule(module: any) {
            try {
                console.log('Handling MoceroUI module:', module.Name);

                // Store module information for dynamic routing
                localStorage.setItem('SelectedModuleId', module.ID.toString());
                localStorage.setItem('SelectedModuleName', module.Name);
                localStorage.setItem('DynamicRouting', 'true');

                // Get module routing preferences
                const routingPreferences = getModuleRoutingPreferences(module.Name);
                localStorage.setItem('ModuleRoutingPreferences', JSON.stringify(routingPreferences));

                // Use router.push() similar to MOPUI patterns
                if (module.ID === 3) {
                    // Inventory module - navigate to current route to trigger dynamic loading
                    await router.push({
                        path: '/',
                        query: {
                            moduleId: module.ID.toString(),
                            moduleName: module.Name,
                            dynamicRouting: 'true'
                        }
                    });
                } else {
                    // Other MoceroUI modules - navigate with query parameters
                    await router.push({
                        path: '/',
                        query: {
                            moduleId: module.ID.toString(),
                            moduleName: module.Name,
                            dynamicRouting: 'true'
                        }
                    });
                }

                console.log(`Router navigation completed for ${module.Name}`);

            } catch (error) {
                console.error('Error handling MoceroUI module:', error);
                throw new Error(`Failed to navigate to ${module.Name} module`);
            }
        }

        // Prepare session data for cross-app navigation
        function prepareSessionForCrossApp(module: any, targetApp: string) {
            try {
                // alert('p');
                // debugger;
                // debugger;

                console.log(`Preparing session for ${targetApp} module:`, module.Name);

                // Store cross-app navigation flags
                localStorage.setItem('SelectedModuleId', module.ID.toString());
                localStorage.setItem('SelectedModuleName', module.Name);
                localStorage.setItem('CrossAppNavigation', 'true');
                localStorage.setItem('SourceApp', 'MoceroUI');
                localStorage.setItem('TargetApp', targetApp);
                localStorage.setItem('DynamicRouting', 'true');

                // Store module-specific routing preferences
                const routingPreferences = getModuleRoutingPreferences(module.Name);
                if (routingPreferences) {
                    localStorage.setItem('ModuleRoutingPreferences', JSON.stringify(routingPreferences));
                }

                console.log(`Session prepared for ${targetApp}:`, {
                    moduleId: module.ID,
                    moduleName: module.Name,
                    routingPreferences
                });

            } catch (error) {
                console.error(`Error preparing session for ${targetApp}:`, error);
                throw new Error(`Failed to prepare session for ${targetApp} navigation`);
            }
        }

        // Navigate to cross-app (simplified MOPUI navigation)
        async function navigateToCrossApp(module: any, targetApp: string) {
            try {
                // Check if we're in development 
                // (different ports) or production (unified server)

                const isDevelopment = window.location.port === '5174';

                let targetUrl: string;

                if (targetApp === 'MOPUI') {
                    if (isDevelopment) {
                        targetUrl = 'http://localhost:5173';
                    } else {
                        targetUrl = `${window.location.origin}`;
                    }

                    // Use MOPLayout with moduleId for all MOPUI modules (same as loginDashboard.vue)
                    // MOPUI uses createWebHistory, not hash routing, so don't include the hash

                    let finalUrl;

                    if (module.ID === 10) {
                        // Navigate to TaskManagement router without query params
                        finalUrl = `${targetUrl}/Task`;
                    } else {
                        finalUrl = `${targetUrl}/MOPLayout?moduleId=${module.ID}`;
                    }

                    console.log(`Navigating to ${targetApp}:`, finalUrl);
                    console.log(`Module to be loaded: ${module.Name} (ID: ${module.ID})`);

                    // Navigate using window.location.href
                    window.location.href = finalUrl;

                } else {
                    // Default fallback for other apps
                    targetUrl = window.location.origin;
                    window.location.href = targetUrl;
                }

            } catch (error) {
                console.error(`Error navigating to ${targetApp}:`, error);
                throw new Error(`Failed to navigate to ${targetApp}`);
            }
        }

        // Get module-specific routing preferences (unified for both MOPUI and MoceroUI)
        function getModuleRoutingPreferences(moduleName: string): any {
            const preferences: { [key: string]: any } = {
                // MOPUI modules
                'accounts': {
                    defaultRoute: '/dashboard',
                    layoutComponent: 'MainLayout',
                    loadMasters: true,
                    type: 'mopui'
                },
                'authorization': {
                    defaultRoute: '/dashboard',
                    layoutComponent: 'MainLayout',
                    loadMasters: true,
                    type: 'mopui'
                },
                'asset management': {
                    defaultRoute: '/dashboard',
                    layoutComponent: 'MainLayout',
                    loadMasters: false,
                    type: 'mopui'
                },
                'task management': {
                    defaultRoute: '/dashboard',
                    layoutComponent: 'MainLayout',
                    loadMasters: false,
                    type: 'mopui'
                },
                // MoceroUI modules
                'crm': {
                    defaultRoute: '/Layout',
                    layoutComponent: 'Layout',
                    loadMasters: true,
                    type: 'moceroui'
                },
                'inventory': {
                    defaultRoute: '/Masters',
                    layoutComponent: 'InventoryLayout',
                    loadMasters: true,
                    type: 'moceroui'
                },
                'billing': {
                    defaultRoute: '/billing',
                    layoutComponent: 'BillingLayout',
                    loadMasters: false,
                    type: 'moceroui'
                },
                'user management': {
                    defaultRoute: '/usermanagement',
                    layoutComponent: 'UserLayout',
                    loadMasters: true,
                    type: 'moceroui'
                },
                'alert engine': {
                    defaultRoute: '/alertengine',
                    layoutComponent: 'AlertEngineLayout',
                    loadMasters: false,
                    type: 'moceroui'
                },
                'communication': {
                    defaultRoute: '/communication',
                    layoutComponent: 'CommunicationLayout',
                    loadMasters: false,
                    type: 'moceroui'
                },
                'queue management': {
                    defaultRoute: '/queuemanagement',
                    layoutComponent: 'QueueLayout',
                    loadMasters: false,
                    type: 'moceroui'
                }
            };

            return preferences[moduleName.toLowerCase()] || {
                defaultRoute: '/',
                layoutComponent: 'Layout',
                loadMasters: false,
                type: 'moceroui'
            };
        }

        // Handle dialog close
        function handleDialogClose() {
            emit('close');
        }

        return {
            isVisible,
            handleModuleNavigation,
            handleDialogClose,
            processedModules,
            // authStore
        };
    }
});
</script>

<style lang="scss" scoped>
/* Loading spinner animation */
.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

/* Module card styles */
.module-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.module-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
}

.module-card.active:hover {
    border-color: #3b82f6;
}

.module-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.module-card.disabled:hover {
    transform: none;
    box-shadow: none;
}

.module-card-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
}

.module-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    flex-shrink: 0;
}

.module-icon-wrapper i {
    font-size: 1.5rem;
}

.lock-overlay {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 20px;
    height: 20px;
    background: #6b7280;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.lock-overlay i {
    font-size: 0.75rem;
    color: white;
}

.module-info {
    flex: 1;
    min-width: 0;
}

.module-name {
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 0.5rem 0;
    line-height: 1.2;
}

.module-description {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0.5rem 0 0 0;
    line-height: 1.4;
}

.module-status {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
}

.module-status.active {
    background: #dcfce7;
    color: #166534;
}

.module-status.inactive {
    background: #fef3c7;
    color: #92400e;
}

.module-status i {
    font-size: 0.75rem;
}

.module-card-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #f3f4f6;
}

.module-card-footer i {
    color: #9ca3af;
    font-size: 1rem;
    transition: color 0.2s ease;
}

.module-card:hover .module-card-footer i {
    color: #3b82f6;
}

.module-card.disabled .module-card-footer i {
    color: #d1d5db;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .module-card-content {
        flex-direction: column;
        text-align: center;
    }

    .module-icon-wrapper {
        align-self: center;
    }
}
</style>
